import React, {
  createContext,
  FC,
  PropsWithChildren,
  ReactElement,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react'
import { login as producerLogin, LoginParams } from '../../Service/auth'
import { getUserFromToken } from '../../utils/getUserFromToken'
import { checkEventAccess } from '../../Service/events'
import { useLocalStorage } from '../useLocalStorage/useLocalStorage'
import { redirectUser } from '../../utils/redirectUser'
import { PAINEL_URL, SSO_URL } from '../../config'
import { instanceAXIOS, setConfig } from '../../Service/api'
import { LOCAL_EXECUTION } from '../../utils/featureFlag'
import { errorRedirect } from '../../utils/redirect'
import { NOT_PERMISSION_BODY, NOT_PERMISSION_TITLE } from '../../utils/message'
import { useNavigate } from 'react-router-dom'
import useBdAppEventBus from '../bdapp/useBdAppEventBus'
import { refreshToken } from '../auth-refresh-token'
import { refreshTokenDestroy } from '../auth-refresh-clean-up'

type AuthStatus = 'idle' | 'pending' | 'logged' | 'not-logged'

interface IAuthContext {
  cookies: any
  status: AuthStatus
  accessToken: string
  user: User
  login: (login: LoginParams) => Promise<void>
  verifyJwt: () => boolean
  hasCookie: () => boolean
  refreshJwtToken: () => void
  authHookWithEvent: (eventId: string, navigate: (url: string) => void) => void
  authHookWithOutEvent: any
  hasThatEvent: (eventId: string, navigate: (url: string) => void) => void
}
export interface ApiObjProps {
  base?: string
}

export interface User {
  id: number
  email: string
  usuario?: string
  sessaoid?: string
  permissoes?: string
  clienteId?: number | null | undefined
  producerEvents?: number[] | undefined
  fullName?: string
  exp: number
  iat: number
}

const AuthContext = createContext<IAuthContext | null>(null)

/**
 * Returns true when the given JWT is present and not expired.
 */
const isValidJwt = (token?: string | null): boolean => {
  if (!token) return false
  try {
    const user: User = getUserFromToken(token)
    if (!user?.exp) return false
    return user.exp > Date.now() / 1000
  } catch {
    return false
  }
}

export const useAuth = (): IAuthContext => {
  const context = useContext(AuthContext)
  if (context == null) {
    throw new Error('useAuth must be used within an AuthContext')
  }

  return context
}

export const AuthProvider: FC<PropsWithChildren<{}>> = ({
  children,
}): ReactElement => {
  const [status, setStatus] = useState<AuthStatus>('idle')
  const [userJwt, setUserJwt] = useLocalStorage('userJWT', '')
  const { on, publish } = useBdAppEventBus()

  const accessToken = userJwt || ''

  // Always-current copy of the token so refreshJwtToken can read the latest
  // value without being recreated (the forced-refresh effect runs once).
  const latestJwtRef = useRef<string>(userJwt || '')
  useEffect(() => {
    latestJwtRef.current = userJwt || ''
  }, [userJwt])

  const refreshJwtToken = useCallback(async (): Promise<void> => {
    try {
      // If a valid (non-expired) token is already present — e.g. supplied by
      // the native app via the `get-user-token` bridge — skip the refresh
      // call. The refresh endpoint requires a `refresh_token` cookie that is
      // not available inside the native WebView, so calling it would fail
      // with "sessão expirada".
      if (isValidJwt(latestJwtRef.current)) {
        log?.success('token-refresh-skipped')
        return
      }
      const newJwtToken = await refreshToken()
      log?.success('token-refresh')
      if (newJwtToken != null) {
        setUserJwt(newJwtToken)
        instanceAXIOS.defaults.headers.common[
          'Authorization'
        ] = `Bearer ${newJwtToken}`
        setConfig('AUTH_TOKEN', newJwtToken)
        // Send the new token to the app via the `logged` event so native listeners update
        publish('logged', { jwt: String(newJwtToken) })
      }
    } catch (err: any) {
      console.error(err, 'on-refresh-token')
    }
  }, [setUserJwt, publish])

  useEffect(() => {
    publish('get-user-token', { status: 'idle' })

    return on('get-user-token', (data) => {
      if (
        data &&
        'status' in data &&
        'jwt' in data &&
        data.status === 'logged' &&
        data.jwt
      ) {
        setStatus('logged')
        setUserJwt(data.jwt)
      }
    })
  }, [on, publish, setUserJwt])

  useEffect(() => {
    const refreshed = { current: false }
    log?.success('refresh-state')
    refreshJwtToken()
    const unsubscribe = on('get-user-token', async (data) => {
      if (
        data &&
        'status' in data &&
        'jwt' in data &&
        data.status === 'logged' &&
        data.jwt &&
        !refreshed.current
      ) {
        log?.success('refresh-state')
        refreshed.current = true
        // A valid token supplied by the native app does not require (and cannot
        // perform) a cookie-based refresh inside the WebView. Keep the ref in
        // sync so refreshJwtToken short-circuits instead of failing.
        latestJwtRef.current = data.jwt
        if (isValidJwt(data.jwt)) {
          return
        }
        await refreshJwtToken()
      }
    })

    publish('get-user-token', { status: 'idle' })

    const unrender = async () => {
      await refreshJwtToken()
    }

    return () => {
      unsubscribe?.()
      unrender?.()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const login = useCallback(
    async (login: LoginParams): Promise<void> => {
      setStatus('pending')
      try {
        const loginResponse = await producerLogin(login)
        if (loginResponse != null) {
          setUserJwt(loginResponse)
          instanceAXIOS.defaults.headers.common[
            'Authorization'
          ] = `Bearer ${loginResponse}`
          setConfig('AUTH_TOKEN', loginResponse)
          setStatus('logged')
        }
      } catch (err: any) {
        setStatus('not-logged')
      }
    },
    [setUserJwt]
  )

  const verifyIfIsValidJwt = useCallback((): boolean => {
    try {
      const user: User = getUserFromToken(userJwt)

      // verify if is expired then return false else return true
      if (!user.exp) return false
      const isExpired = user.exp < Date.now() / 1000

      return !isExpired
    } catch (err: any) {
      return false
    }
  }, [userJwt])

  const hasCookie = useCallback((): boolean => {
    // VERIFY IF BROWSER HAS THE CORRECT COOKIE
    return userJwt != null
  }, [userJwt])

  const hasThatEvent = useCallback(
    (eventId: string, navigate: any): void => {
      // VERIFY IF USER HAS THAT EVENT ACCESS
      //
      // Asks the API instead of reading the `producerEvents` claim. That claim is
      // capped at 300 ids to fit the auth cookie, so for producers with more links
      // than that it reported their own newest events as inaccessible — and it
      // never reflected a link added or revoked after the token was issued.
      if (!hasCookie()) return

      const user = getUserFromToken(userJwt)

      // Operators are not scoped to a single producer's events.
      if (user.clienteId === null) return

      void (async () => {
        try {
          const allowed = await checkEventAccess(eventId)
          if (!allowed) {
            navigate(-1)
          }
        } catch (error) {
          // A check that failed to run is not a denial. Bouncing here would throw
          // users out of events they do own whenever the network blips, so the page
          // is left up: every event-scoped endpoint enforces ownership itself and
          // answers NOT_PERMITED when access is genuinely absent.
          console.error('event access check failed', error)
        }
      })()
    },
    [hasCookie, userJwt]
  )

  const authHookWithEvent = useCallback(
    (eventId: string, navigate: any): void => {
      console.log('authHookWithEvent', eventId, verifyIfIsValidJwt())
      if (!verifyIfIsValidJwt()) {
        const url = new URL(SSO_URL)
        console.log('authHookWithEvent redirecting to auth page')
        url.searchParams.set('redirect_uri', window.location.origin + '/auth')
        redirectUser(url.toString())

        console.error(
          'Você não tem permissão para acessar esse evento authHookWithEvent'
        )
      } else {
        hasThatEvent(eventId, navigate)
      }
    },
    [verifyIfIsValidJwt, hasThatEvent]
  )

  const authHookWithOutEvent = useCallback((): void => {
    if (!verifyIfIsValidJwt()) {
      const url = new URL(SSO_URL)
      console.log('authHookWithOutEvent redirecting to auth page')
      url.searchParams.set('redirect_uri', window.location.origin + '/auth')
      redirectUser(url.toString())

      console.error(
        'Você não tem permissão para acessar esse evento authHookWithOutEvent'
      )
    }
  }, [verifyIfIsValidJwt])

  useEffect(() => {
    log?.success('verify')
    if (verifyIfIsValidJwt()) {
      if (userJwt) {
        // need verify in dev
        instanceAXIOS.defaults.headers.common[
          'Authorization'
        ] = `Bearer ${userJwt}`
        setConfig('AUTH_TOKEN', userJwt)
        setStatus('logged')
      }
    }
  }, [userJwt, verifyIfIsValidJwt])

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!LOCAL_EXECUTION && status === 'idle' && !accessToken) {
        setStatus('not-logged')
      }
    }, 2000)

    return () => clearTimeout(timeout)
  }, [accessToken, status])

  const user = useMemo(() => {
    const authToken = LOCAL_EXECUTION
      ? process.env.REACT_APP_LOCAL_EXECUTION_TOKEN
      : userJwt
    return getUserFromToken(authToken)
  }, [userJwt])

  return (
    <AuthContext.Provider
      value={{
        status,
        accessToken,
        cookies: { userJWT: userJwt },
        verifyJwt: verifyIfIsValidJwt,
        hasCookie,
        hasThatEvent,
        authHookWithEvent,
        authHookWithOutEvent,
        refreshJwtToken,
        login,
        user,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useUser = (): User => {
  const authContext = useAuth()
  const { cookies } = authContext
  const authToken = LOCAL_EXECUTION
    ? process.env.REACT_APP_LOCAL_EXECUTION_TOKEN
    : cookies.userJWT
  const user: User = getUserFromToken(authToken)

  return user
}

export const useHasAccess = (value: string): boolean => {
  const user = useUser()

  if (user?.permissoes?.split(',').includes(value)) {
    return true
  }

  return false
}

export const useGuard = (
  values: string[] = ['DP1', 'DP2', 'DP3', 'DP4', 'DP5', 'DP6', 'DP7', 'DP8'],
  callback?: () => void
): void => {
  const user = useUser()
  const navigate = useNavigate()
  useEffect(() => {
    if (user?.id) {
      values.forEach((value: string) => {
        if (!user?.permissoes?.split(',').includes(value)) {
          if (callback) {
            callback()
          } else {
            errorRedirect(
              '/error',
              {
                message: NOT_PERMISSION_BODY,
                tittle: NOT_PERMISSION_TITLE,
              },
              navigate
            )
          }
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])
}

export const useGuardOr = (
  values: string[] = ['DP1', 'DP2', 'DP3', 'DP4', 'DP5', 'DP6', 'DP7', 'DP8'],
  callback?: () => void
): void => {
  const user = useUser()
  const navigate = useNavigate()

  useEffect(() => {
    if (user?.id) {
      const permissoes = user?.permissoes?.split(',') || []
      const temPermissao = values.some((value) => permissoes.includes(value))

      if (!temPermissao) {
        if (callback) {
          callback()
        } else {
          errorRedirect(
            '/error',
            {
              message: NOT_PERMISSION_BODY,
              tittle: NOT_PERMISSION_TITLE,
            },
            navigate
          )
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])
}

export const useLogout = (): any => {
  const [, , removeUserJwt] = useLocalStorage('userJWT', '')

  const dispatch = async () => {
    removeUserJwt()
    log!.success('BEFORE-DESTROY')
    await refreshTokenDestroy()
    const ssoUrl = process.env.REACT_APP_SSO_URL

    if (ssoUrl) {
      redirectUser(`${ssoUrl}logout`)
    } else {
      redirectUser(`${PAINEL_URL}painel?action=logout`)
    }
  }

  return {
    dispatch,
  }
}
